import {
  CheckIcon,
  FileCsvIcon,
  FileDocIcon,
  FilePdfIcon,
  FileTextIcon,
  PencilSimpleIcon,
  PlusIcon,
  SparkleIcon,
  UploadSimpleIcon,
} from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import React, { useRef } from 'react';
import { toast } from 'sonner';

import { PlanContextSection } from './PlanContextSection';
import { ProjectConversationsList } from './ProjectConversationsList';
import { WorkflowSummarySection } from './WorkflowSummarySection';

import { PlatformBrandIcon } from '@/components/icons/platform-brand-icon';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { FilesLineIcon } from '@/components/ui/illustrations';
import { InlineFieldEdit, type InlineFieldEditHandle } from '@/components/ui/inline-field-edit';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  PLAN_GOAL_MAX_LENGTH,
  PLAN_NOTES_MAX_LENGTH,
  sanitizePlanText,
} from '@/lib/text/sanitize-plan-text';
import {
  ICON_BUTTON,
  ITEM_META,
  ITEM_TITLE,
  MOBILE_HIT_AREA,
  PANEL_CONTAINER_INSET,
  PANEL_DIVIDER_INSET,
  SECTION_TITLE,
  THUMBNAIL_SQUARE,
} from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';
import { campaignService } from '@/services/data/CampaignService';
import type { CampaignWithCoverage } from '@/services/data/CampaignService';

type FileMeta = {
  icon: typeof FilePdfIcon;
  badge: string;
  kind: string;
};

function resolveFileMeta(name: string): FileMeta {
  const lower = name.toLowerCase();
  if (lower.endsWith('.pdf')) return { icon: FilePdfIcon, badge: 'PDF', kind: 'PDF' };
  if (lower.endsWith('.csv')) return { icon: FileCsvIcon, badge: 'CSV', kind: 'CSV' };
  if (lower.endsWith('.md')) return { icon: FileTextIcon, badge: 'MD', kind: 'Markdown' };
  if (lower.endsWith('.txt')) return { icon: FileTextIcon, badge: 'TXT', kind: 'Text' };
  if (lower.endsWith('.docx') || lower.endsWith('.doc'))
    return { icon: FileDocIcon, badge: 'DOC', kind: 'Document' };
  return { icon: FileTextIcon, badge: 'FILE', kind: 'File' };
}

function formatFileMeta(asset: Record<string, unknown>): string {
  const size = typeof asset.size === 'number' ? (asset.size as number) : null;
  const lineCount = typeof asset.line_count === 'number' ? (asset.line_count as number) : null;
  if (lineCount) return `${lineCount.toLocaleString()} lines`;
  if (size) {
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  }
  return '';
}

interface PlanContextSidebarProps {
  campaign: CampaignWithCoverage | null;
  filteredAssets: Array<{ id: string; raw: Record<string, unknown> }>;
  onUploadClick: () => void;
  onUploadFiles?: (files: File[]) => void;
  /**
   * Opens the workflow template library so the user can assign a workflow.
   * When omitted (or the campaign has no workflow), the dashed "Assign an
   * AI Workflow" button is rendered as a soft no-op surface — useful inside
   * the chat-page hub (ProjectHubMount) where the library lives in a
   * different mount.
   */
  onOpenWorkflowLibrary?: () => void;
  /**
   * Render mode:
   * - `'detail'` (default, used on `/projects/:id`): always render Audience + Instructions
   *   sections so the user has the affordance to ADD them. Empty-state CTAs render
   *   inside each section when content is missing.
   * - `'hub'` (PH-1, used on `/create` via ProjectHubMount): same as 'detail' minus
   *   ProjectConversationsList. The user is in a conversation on /create — listing
   *   other conversations would navigate away mid-flow.
   */
  variant?: 'detail' | 'hub';
  /**
   * PH-1 v2 (Issue 2B from design review): variant-specific empty-state copy.
   * The 'hub' variant lives on /create where the user is mid-conversation;
   * surface text should reflect that ("Drop files into this conversation"
   * vs the detail rail's broader "Drop files to add to this project"). When
   * omitted, falls back to the detail-rail strings.
   */
  copyOverrides?: {
    filesEmptyTitle?: string;
    filesEmptyDescription?: string;
  };
}

// Match ProjectsPage <input accept="..."> — keep these in sync.
const ACCEPTED_DROP_MIMES = new Set([
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/webp',
  'image/gif',
  'video/mp4',
]);

export const PlanContextSidebar: React.FC<PlanContextSidebarProps> = ({
  campaign,
  filteredAssets,
  onUploadClick,
  onUploadFiles,
  onOpenWorkflowLibrary,
  variant = 'detail',
  copyOverrides,
}) => {
  const queryClient = useQueryClient();
  const notesEditRef = useRef<InlineFieldEditHandle>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = React.useState(false);
  const [dropAnnouncement, setDropAnnouncement] = React.useState('');
  const [notesEditing, setNotesEditing] = React.useState(false);
  const dragDepthRef = useRef(0);

  // Drag-drop handlers — native HTML5, no extra dep. Counter pattern handles
  // child enter/leave events. Visual treatment follows the AI-Temporary
  // contract (spec §AI-Temporary): bg-skill/[0.04] wash + dashed --skill
  // border on the empty-state drop target.
  const handleDragEnter = React.useCallback((e: React.DragEvent<HTMLDivElement>) => {
    if (!e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current += 1;
    setIsDragging(true);
  }, []);

  const handleDragOver = React.useCallback((e: React.DragEvent<HTMLDivElement>) => {
    if (!e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
  }, []);

  const handleDragLeave = React.useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setIsDragging(false);
  }, []);

  const handleDrop = React.useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      dragDepthRef.current = 0;
      setIsDragging(false);
      const incoming = Array.from(e.dataTransfer.files ?? []);
      const accepted = incoming.filter((f) => ACCEPTED_DROP_MIMES.has(f.type));
      const rejected = incoming.length - accepted.length;
      if (accepted.length === 0) {
        toast.error('Only images and MP4 video are accepted.');
        setDropAnnouncement('No accepted files in drop.');
        return;
      }
      if (rejected > 0) {
        toast.warning(`${rejected} file${rejected === 1 ? '' : 's'} skipped — unsupported type.`);
      }
      if (onUploadFiles) {
        onUploadFiles(accepted);
        setDropAnnouncement(
          `${accepted.length} file${accepted.length === 1 ? '' : 's'} added to upload.`,
        );
      } else {
        onUploadClick();
        setDropAnnouncement('Opening file picker…');
      }
    },
    [onUploadFiles, onUploadClick],
  );

  if (!campaign) return null;

  const hasNotes = !!campaign.notes?.trim();
  const hasFiles = filteredAssets.length > 0;

  const updateCampaignField = async (key: 'notes' | 'goal', value: string, label: string) => {
    const cleaned = sanitizePlanText(value, {
      maxLength: key === 'goal' ? PLAN_GOAL_MAX_LENGTH : PLAN_NOTES_MAX_LENGTH,
      multiline: true,
    });
    const res = await campaignService.updateCampaign(campaign.id, { [key]: cleaned });
    if (res.error) {
      toast.error(`Couldn't save: ${res.error}`);
      return;
    }
    await queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    toast.success(`${label} saved`);
  };

  const handlePickFromDevice = () => {
    fileInputRef.current?.click();
  };

  const handleDeviceFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const incoming = Array.from(e.target.files ?? []);
    if (incoming.length === 0) return;
    if (onUploadFiles) onUploadFiles(incoming);
    else onUploadClick();
    e.target.value = '';
  };

  const showNotes = variant === 'detail' || hasNotes;

  // Wrapper is variant-aware:
  // - 'detail' (/projects/:id): card-on-page pattern. PANEL_CONTAINER_INSET
  //   gives rounded-xl + border + bg-card/40 — the rail is one element among
  //   many on a wider page so the card visual creates focus.
  // - 'hub' (/create): the OUTER aside is the flush FloatingPanel (bg-card, no
  //   radius/shadow — the seam is the ResizablePanel handle, owned in AppLayout).
  //   Inside it, sections sit directly with hairline dividers — no nested card,
  //   no floating-card chrome. Tight column. (Redesigned 2026-06-15.)
  const wrapperClass =
    variant === 'hub' ? 'flex h-full flex-col divide-y divide-border/40' : PANEL_CONTAINER_INSET;

  // Audit pass 3 — hub variant DROPS sections that don't belong in the
  // /create chat context: Instructions, Knowledge, Documents,
  // Conversations are project-level concerns surfaced on /projects/:id.
  // The chat hub is for: "what workflow is running, what files belong to
  // this conversation". User feedback: "way too much going on". Less,
  // but better.
  const isHub = variant === 'hub';

  return (
    <div className={wrapperClass}>
      {/* Workflow — read-only summary. The execution surface (asset preview,
          Approve / Regenerate, advance-step actions) lives only in the
          chat-page Inspector per docs/superpowers/specs/2026-05-02-ugc-workflow-design.md § 4.
          The hub is a launcher: read state, route to chat.

          Audit pass 6: hub variant shows the FULL step list (Claude Code
          Progress pattern). /projects/:id detail keeps the compact
          1-line summary because that page surfaces other rich progress
          info (calendar grid, posts list) and the rail is supporting. */}
      <WorkflowSummarySection
        campaign={campaign}
        onOpenWorkflowLibrary={onOpenWorkflowLibrary}
        showSteps={isHub}
      />

      {showNotes && !isHub && (
        <>
          <Separator className={PANEL_DIVIDER_INSET} />
          <section className="p-5">
            <div className="mb-3 flex items-center justify-between">
              <h3 className={SECTION_TITLE}>Instructions</h3>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    className={cn(ICON_BUTTON, MOBILE_HIT_AREA)}
                    aria-label={
                      notesEditing
                        ? 'Done editing instructions'
                        : hasNotes
                          ? 'Edit instructions'
                          : 'Add instructions'
                    }
                    onClick={() => {
                      if (notesEditing) {
                        notesEditRef.current?.stopEditing();
                        setNotesEditing(false);
                      } else {
                        notesEditRef.current?.startEditing();
                        setNotesEditing(true);
                      }
                    }}
                  >
                    {notesEditing ? (
                      <CheckIcon className="size-3.5" />
                    ) : (
                      <PencilSimpleIcon className="size-3.5" />
                    )}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  {notesEditing ? 'Done' : hasNotes ? 'Edit instructions' : 'Add instructions'}
                </TooltipContent>
              </Tooltip>
            </div>
            <InlineFieldEdit
              ref={notesEditRef}
              value={campaign.notes || ''}
              onSave={(next) => updateCampaignField('notes', next, 'Instructions')}
              onEditingChange={setNotesEditing}
              multiline
              rows={3}
              maxLength={PLAN_NOTES_MAX_LENGTH}
              placeholder='e.g. "No emojis. Keep posts under 140 characters and end with a question. Voice is warm but direct — never salesy."'
              ariaLabel="custom instructions"
              hideAction
            />
          </section>
        </>
      )}

      {!isHub && campaign?.id && <PlanContextSection campaign={campaign} />}

      {campaign.id && !isHub && <ProjectConversationsList projectId={campaign.id} />}

      {/* Files — HUB VARIANT ONLY. On /projects/:id the assigned media's one
          home is the workbench Library tab (which carries the count and the
          "Add files" toolbar action); listing the same items here as doc-icon
          rows double-listed every asset in one viewport (user-reported,
          2026-07-08). The hub keeps it: chat context files belong in the
          /create rail. */}
      {isHub && (
        <section
          className={cn(
            'relative p-5 transition-colors duration-200 ease-fluid',
            isDragging && 'bg-skill/[0.04]',
          )}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          aria-label="Files panel — drop reference files here"
        >
          {/* Screen-reader-only live region for drop outcome */}
          <div className="sr-only" aria-live="polite" aria-atomic="true">
            {dropAnnouncement}
          </div>
          <div className="flex items-center justify-between mb-2.5">
            <h3 className={cn(SECTION_TITLE, 'flex items-center gap-1.5')}>
              <span>
                Files
                {filteredAssets.length > 0 && (
                  <span className="ml-1.5 text-detail text-muted-foreground/60 tabular-nums font-normal">
                    {filteredAssets.length}
                  </span>
                )}
              </span>
              {isDragging && (
                <SparkleIcon
                  className="size-3 text-skill/70 animate-[subtle-pulse_2s_ease-in-out_infinite]"
                  weight="duotone"
                  aria-hidden="true"
                />
              )}
            </h3>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className={cn(ICON_BUTTON, MOBILE_HIT_AREA)} aria-label="Add file">
                  <PlusIcon className="size-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                sideOffset={6}
                className="w-56 rounded-xl border border-border/60 bg-popover/95 backdrop-blur-xl shadow-elevation-3 p-1"
              >
                <DropdownMenuItem
                  onClick={handlePickFromDevice}
                  className="gap-2 rounded-sm px-2 py-1.5 text-caption"
                >
                  <UploadSimpleIcon className="size-4 text-muted-foreground/70" weight="regular" />
                  <span>Upload from device</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() =>
                    window.dispatchEvent(
                      new CustomEvent('libraryAction', { detail: { action: 'google-drive' } }),
                    )
                  }
                  className="gap-2 rounded-sm px-2 py-1.5 text-caption"
                >
                  <PlatformBrandIcon platform="google-drive" className="size-4" />
                  <span>Google Drive</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/png,image/jpeg,image/jpg,image/webp,image/gif,video/mp4"
              className="sr-only"
              onChange={handleDeviceFiles}
              aria-hidden="true"
              tabIndex={-1}
            />
          </div>
          {hasFiles ? (
            <div>
              <div className="-mx-5">
                {filteredAssets.slice(0, 4).map((asset) => {
                  const raw = asset.raw as Record<string, unknown>;
                  const name = (raw?.name as string) || 'Untitled';
                  const meta = resolveFileMeta(name);
                  const Icon = meta.icon;
                  const metaText = formatFileMeta(raw);
                  return (
                    <div
                      key={asset.id}
                      className={cn(
                        'group flex items-center gap-3 px-5 py-2.5',
                        'hover:bg-muted/40 transition-colors duration-150 cursor-pointer',
                      )}
                    >
                      <span className={THUMBNAIL_SQUARE}>
                        <Icon className="size-3.5 text-muted-foreground/60" weight="regular" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className={cn(ITEM_TITLE, 'truncate')}>{name}</p>
                        {metaText && <p className={ITEM_META}>{metaText}</p>}
                      </div>
                    </div>
                  );
                })}
                {filteredAssets.length > 4 && (
                  <p className="text-detail text-muted-foreground/60 px-5 pt-1">
                    +{filteredAssets.length - 4} more file
                    {filteredAssets.length - 4 === 1 ? '' : 's'}
                  </p>
                )}
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={onUploadClick}
              aria-label="Add reference file"
              aria-describedby="files-empty-description"
              className={cn(
                'group/drop flex flex-col w-full items-center justify-center rounded-lg border border-dashed border-border/50 bg-transparent transition duration-200 ease-fluid',
                'hover:border-foreground/30 hover:bg-muted/30',
                'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring/40',
                isDragging && 'border-skill/50 bg-skill/[0.06]',
                // Audit pass 3 — hub variant uses tight padding + smaller
                // icon. The /projects/:id Files page uses Files as primary
                // content and earns a generous empty state. The /create
                // hub uses Files as a tertiary affordance and should not
                // dominate the rail.
                isHub ? 'gap-2 px-4 py-4' : 'gap-3 px-4 py-6',
              )}
            >
              <FilesLineIcon
                className={cn(
                  'transition-colors duration-200',
                  isHub ? 'size-7' : 'size-12',
                  isDragging
                    ? 'text-skill/80'
                    : 'text-muted-foreground/40 group-hover/drop:text-muted-foreground/60',
                )}
              />
              <span
                id="files-empty-description"
                className={cn(
                  'text-center leading-snug transition-colors duration-200',
                  isHub ? 'text-micro' : 'text-detail',
                  isDragging ? 'text-skill/85 font-medium' : 'text-muted-foreground/70',
                )}
              >
                {isDragging
                  ? (copyOverrides?.filesEmptyTitle ?? 'Drop to add to this project')
                  : (copyOverrides?.filesEmptyDescription ??
                    'Drop images, video, briefs, anything that informs the work.')}
              </span>
            </button>
          )}
        </section>
      )}
    </div>
  );
};
